import { ISalesFunnel, ISalesFunnelStage } from '@user/store/salesFunnel'
import { useCallback } from 'react'

export function useSalesFunnel(
  allSalesFunnels: ISalesFunnel[],
  allStages: ISalesFunnelStage[],
) {
  const assignSalesFunnelAndDefaultStage = useCallback(
    (record) => {
      const newRecord = { ...record }
      const { salesFunnelName, salesFunnelId } = newRecord.row

      if (salesFunnelName?.value) {
        const funnelName = salesFunnelName.value.replace('(Default)', '').trim()
        const salesFunnel = allSalesFunnels.find((x) => x.name === funnelName)

        salesFunnelId.value = salesFunnel?.id || ''
      } else {
        const salesFunnel = allSalesFunnels.find((x) => x.isDefault)

        if (salesFunnel) {
          salesFunnelName.value = `(Default) ${salesFunnel.name}`
          salesFunnelId.value = salesFunnel.id
        }
      }

      return newRecord
    },
    [allSalesFunnels],
  )

  const assignStagesForSalesFunnel = useCallback(
    (record, stageField: string, sectionType: string) => {
      const newRecord = { ...record }
      const { salesFunnelId } = newRecord.row

      const stageNameField = newRecord.row[stageField]

      if (!stageNameField) return newRecord

      stageNameField.selectOptions = salesFunnelId?.value
        ? allStages
            .filter(
              (x) =>
                x.salesFunnelId === salesFunnelId.value &&
                !x.closed &&
                x.sectionType === sectionType,
            )
            .map((b) => ({ label: b.name, value: b.name }))
        : []

      return newRecord
    },
    [allStages],
  )

  const assignFunnelStageIds = useCallback(
    (record) => {
      const newRecord = { ...record }
      const { salesFunnelId } = newRecord.row

      const salesFunnelIdValue = salesFunnelId?.value

      const stages = [
        {
          stageName: 'opportunityFunnelStageName',
          stageId: 'opportunityFunnelStageId',
          sectionType: 'Opportunity',
        },
        {
          stageName: 'leadFunnelStageName',
          stageId: 'leadFunnelStageId',
          sectionType: 'Lead',
        },
      ]

      stages.forEach(({ stageName, stageId, sectionType }) => {
        if (!newRecord.row[stageName] || !newRecord.row[stageId]) return

        const stageNameValue = newRecord.row[stageName].value

        assignStagesForSalesFunnel(newRecord, stageName, sectionType)

        if (!stageNameValue && salesFunnelIdValue) {
          const funnelStage = allStages.filter(
            (x) =>
              x.salesFunnelId === salesFunnelIdValue &&
              x.sectionType === sectionType,
          )?.[0]

          if (funnelStage) {
            newRecord.row[stageName].value = funnelStage.name
            newRecord.row[stageId].value = funnelStage.id
          }
        } else {
          newRecord.row[stageId].value =
            allStages.find(
              (stage) =>
                stage.salesFunnelId === salesFunnelIdValue &&
                stage.name === stageNameValue &&
                stage.sectionType === sectionType,
            )?.id ?? ''
        }

        return newRecord
      })

      return newRecord
    },
    [allStages, assignStagesForSalesFunnel],
  )

  return {
    assignSalesFunnelAndDefaultStage,
    assignFunnelStageIds,
  }
}
